import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import type { EscritorioAdmin } from '@/lib/plataforma_admin';
import { Button } from '@/components/ui';

interface EncerramentoControleProps {
  escritorio: EscritorioAdmin;
  salvando: boolean;
  onDefinir: (escritorioId: string, encerrado: boolean) => Promise<unknown>;
}

function _quemPerdeOAcesso(total: number): string {
  if (total === 0) return ptBR.admin.encerrarConfirmacaoNinguem;
  if (total === 1) return ptBR.admin.encerrarConfirmacaoUmaPessoa;
  return ptBR.admin.encerrarConfirmacaoPessoas.replace('{total}', String(total));
}

/**
 * Encerrar / reabrir um escritório pelo console (E69.F1, ADR-0072). // ux-neutrality:allow doc-comment
 *
 * Encerrar é a única ação da tela que alcança VÁRIAS contas de uma vez, por // ux-neutrality:allow doc-comment
 * isso pede confirmação em dois passos e a confirmação diz o efeito em números: // ux-neutrality:allow doc-comment
 * quantas pessoas perdem o acesso agora, e que casos e histórico ficam. Reabrir // ux-neutrality:allow doc-comment
 * não reativa ninguém, então não pede confirmação. O escritório da plataforma // ux-neutrality:allow doc-comment
 * não recebe o controle — o servidor recusaria de qualquer forma (422). // ux-neutrality:allow doc-comment
 */
export function EncerramentoControle({
  escritorio,
  salvando,
  onDefinir,
}: EncerramentoControleProps) {
  const [confirmando, setConfirmando] = useState(false);

  if (escritorio.is_platform) return null;

  if (!escritorio.ativo) {
    return (
      <Button
        variant="secondary"
        size="sm"
        type="button"
        disabled={salvando}
        onClick={() => void onDefinir(escritorio.id, false)}
        data-testid={`reabrir-${escritorio.id}`}
      >
        {ptBR.admin.reabrirEscritorio}
      </Button>
    );
  }

  if (confirmando) {
    const pergunta = ptBR.admin.encerrarConfirmacao
      .replace('{nome}', escritorio.nome)
      .replace('{pessoas}', _quemPerdeOAcesso(escritorio.total_usuarios));
    return (
      <span
        className="flex flex-wrap items-center gap-1.5"
        role="alert"
        data-testid={`encerrar-confirmacao-${escritorio.id}`}
      >
        <span className="text-body-sm text-fg-muted">{pergunta}</span>
        <Button
          variant="danger"
          size="sm"
          type="button"
          disabled={salvando}
          onClick={() => {
            setConfirmando(false);
            void onDefinir(escritorio.id, true);
          }}
          data-testid={`encerrar-confirmar-${escritorio.id}`}
        >
          {ptBR.admin.confirmarSim}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={() => setConfirmando(false)}
          data-testid={`encerrar-cancelar-${escritorio.id}`}
        >
          {ptBR.admin.confirmarNao}
        </Button>
      </span>
    );
  }

  return (
    <Button
      variant="danger"
      size="sm"
      type="button"
      disabled={salvando}
      onClick={() => setConfirmando(true)}
      data-testid={`encerrar-${escritorio.id}`}
    >
      {ptBR.admin.encerrarEscritorio}
    </Button>
  );
}
